import { forwardRef, useState, useEffect, useMemo, useRef } from "react";
import type { Ref, ChangeEvent, ReactNode, ForwardedRef } from "react";
import type { MenuPosition, MenuPlacement } from "react-select";
import { cx } from "../../../utils";
import { Label } from "../Label";
import { InputSize, getDaysInMonth } from "./utils";
import { DayInput } from "./day-input";
import { MonthSelect } from "./month-select";
import { YearInput } from "./year-input";

interface InputProps {
  label?: string | ReactNode;
  size?: InputSize;
  id: string;
  placeholder?: string;
  type?: string;
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  className?: string;
  error?: string;
  maxLength?: number;
  max?: number;
  checked?: boolean;
  focusOnError?: boolean;
  defaultValue?: string;
  borderLess?: boolean;
  readOnly?: boolean;
  list?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  /** Renders a trailing `*` on the label. */
  showRequired?: boolean;
  hideOptional?: boolean;
  ignoreDay?: boolean;
  locale?: string;
  legacyErrorMargins?: boolean;

  // For month dropdown
  menuPosition?: MenuPosition;
  menuPlacement?: MenuPlacement;
}

const translateInvalidDate = (locale: string) => {
  switch (locale) {
    case "es":
      return "Fecha inválida";
    case "en":
    default:
      return "Invalid date";
  }
};

// Prefill on this won't work.

const DateInputComponent = (
  props: InputProps,
  ref?: ForwardedRef<HTMLInputElement>
) => {
  const {
    label,
    id,
    // placeholder,
    // type,
    size = InputSize.BASE,
    onChange,
    className,
    error,
    value,
    readOnly,
    focusOnError,
    borderLess,
    ignoreDay,
    required,
    showRequired,
    hideOptional,
    locale = "en",
    menuPosition,
    menuPlacement,
    legacyErrorMargins = true
    // ...rest
  } = props;

  const yearRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (focusOnError && error) {
      (
        yearRef as {
          current?: {
            focus?: () => void;
          };
        }
      ).current?.focus?.();
    }
  }, [focusOnError, error]);

  useEffect(() => {
    if (typeof value === "string" && value) {
      // trim T part from ISO date
      const date = value.includes("T") ? value.split("T")[0] : value;
      const [y, m, d] = date.includes("-") ? date.split("-") : date.split("/");

      setYear(y);
      setMonth(m);
      setDay(ignoreDay ? "01" : parseInt(d).toString());
    }
  }, [value]);

  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const isInitialMount = useRef(true);

  // Validate day - like 30 february / 31 september
  const isValidDay = useMemo(() => {
    if (ignoreDay) return true;
    // Ignore check if month or year is not set
    if (!month || !year) return true;
    return Number(day) <= getDaysInMonth(Number(year), Number(month));
  }, [day, month, year, ignoreDay]);

  const transformedDay = (dayValue: string) => {
    if (ignoreDay) return "01";
    if (!dayValue) return "";
    return dayValue.length === 2 ? dayValue : dayValue.padStart(2, "0");
  };

  const isoDate = useMemo(() => {
    if (!year || !month || (!day && !ignoreDay) || !isValidDay) return "";

    const d = new Date(
      Number(year),
      Number(month) - 1,
      ignoreDay ? 1 : Number(day)
    );

    if (d instanceof Date && !isNaN(Number(d))) {
      const dayStr = transformedDay(day);
      return `${year}-${month}-${dayStr}`;
    }

    return "";
  }, [day, month, year, ignoreDay, isValidDay]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onChange?.({
      // @ts-expect-error -- well, that's fine
      target: { id, value: isoDate } as unknown as EventTarget
    });
    // onChange identity is intentionally excluded — we only react to isoDate changes
  }, [isoDate, id]);

  return (
    <>
      <input id={id} name={id} ref={ref} type="hidden" value={isoDate} />

      {!borderLess && label ? (
        <Label
          htmlFor={id}
          locale={locale}
          showRequired={showRequired}
          showOptional={Boolean(!required && !hideOptional)}
        >
          {label}
        </Label>
      ) : null}

      <div className={cx("flex gap-2", className)}>
        <MonthSelect
          menuPosition={menuPosition}
          menuPlacement={menuPlacement}
          locale={locale}
          error={Boolean(error || !isValidDay)}
          onChange={setMonth}
          onFocus={() => {
            yearRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
          }}
          readOnly={readOnly}
          legacyErrorMargins={legacyErrorMargins}
          size={size}
          value={month}
        />
        {ignoreDay ? null : (
          <DayInput
            locale={locale}
            error={Boolean(error || !isValidDay)}
            onChange={setDay}
            readOnly={readOnly}
            size={size}
            value={day}
            legacyErrorMargins={legacyErrorMargins}
          />
        )}
        <YearInput
          locale={locale}
          error={Boolean(error || !isValidDay)}
          onChange={setYear}
          readOnly={readOnly}
          ref={yearRef as Ref<HTMLInputElement>}
          size={size}
          value={year}
          legacyErrorMargins={legacyErrorMargins}
        />
      </div>

      {error || !isValidDay ? (
        <p className="text-danger-500 text-xs mt-2">
          {!isValidDay ? translateInvalidDate(locale) : error}
        </p>
      ) : null}
    </>
  );
};

const DateInput = forwardRef(DateInputComponent);
DateInput.displayName = "DateInput";

export { DateInput, InputSize };
